"use client";

import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { Button } from "@west-tennessee-consulting/ui/components/button";
import { Loader2, AlertCircle } from "lucide-react";
import { useCallback, useState } from "react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export type ExportFormat = "csv" | "pdf" | "docx";
export type ExportScope = "current-page" | "all-filtered";

interface ExportScopeModalProps {
  isOpen: boolean;
  entityName: string;
  currentPageCount: number;
  totalCount: number;
  isGrouped: boolean;
  groupByField?: string;
  // Dual grouping support
  dualGrouping?: {
    primary?: string | null;
    secondary?: string | null;
  };
  isLoading?: boolean;
  hasActiveFilters?: boolean;
  exportProgress?: number; // 0-100
  onExport: (
    scope: ExportScope,
    exportFormat: ExportFormat,
    format?: "structured" | "hierarchical"
  ) => void;
  onCancel: () => void;
}

export const ExportScopeModal = ({
  isOpen,
  entityName,
  currentPageCount,
  totalCount,
  isGrouped,
  groupByField,
  dualGrouping,
  isLoading = false,
  hasActiveFilters = false,
  exportProgress = 0,
  onExport,
  onCancel,
}: ExportScopeModalProps) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("csv");

  const handleCurrentPage = useCallback(() => {
    onExport("current-page", selectedFormat, "structured");
  }, [onExport, selectedFormat]);

  const handleAllFiltered = useCallback(() => {
    onExport("all-filtered", selectedFormat, "structured");
  }, [onExport, selectedFormat]);

  const hasMorePages = totalCount > currentPageCount;
  const showProgress = isLoading && exportProgress > 0;

  // Determine grouping information
  const isDualGrouping = dualGrouping?.primary && dualGrouping?.secondary;
  const primaryGroup = dualGrouping?.primary || groupByField;
  const secondaryGroup = dualGrouping?.secondary;

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={onCancel}
      title={`Export ${entityName}`}
      maxWidth="md"
    >
      <div className="space-y-4">
        {isGrouped && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📊</span>
              <div className="flex-1">
                {isDualGrouping ? (
                  <div>
                    <p className="font-semibold text-blue-900 dark:text-blue-100">
                      Dual Grouping Active
                    </p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="inline-block w-2 h-2 bg-card-500 rounded-full"></span>
                        <span className="font-medium">Primary:</span>
                        <span className="bg-card-100 dark:bg-card-800 px-2 py-1 rounded text-xs font-mono">
                          {primaryGroup}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                        <span className="font-medium">Secondary:</span>
                        <span className="bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded text-xs font-mono">
                          {secondaryGroup}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold text-blue-900 dark:text-blue-100">
                      Single Grouping Active
                    </p>
                    <div className="mt-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="inline-block w-2 h-2 bg-card-500 rounded-full"></span>
                        <span className="font-medium">Grouping by:</span>
                        <span className="bg-card-100 dark:bg-card-800 px-2 py-1 rounded text-xs font-mono">
                          {primaryGroup}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-3 p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
                  <p className="text-sm font-medium text-green-900 dark:text-green-100 mb-2">
                    📈 Spreadsheet Analysis Ready
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300">
                    Export will include separate columns for each grouping level, making it perfect
                    for pivot tables, filtering, and data analysis.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {hasMorePages && (
          <div className="bg-amber-50 dark:bg-amber-950 p-3 rounded-md text-sm border border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-100">
                  {hasActiveFilters ? "Filtered Results" : "Large Dataset"}
                </p>
                <p className="text-xs opacity-75 mt-1">
                  {hasActiveFilters
                    ? `Showing ${currentPageCount} of ${totalCount} filtered items. "Export All" will fetch and export all ${totalCount} items.`
                    : `Total of ${totalCount} items available. "Export All" will fetch and export all items.`}
                </p>
              </div>
            </div>
          </div>
        )}

        {showProgress && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Exporting...</span>
              <span className="text-xs opacity-70">{exportProgress}%</span>
            </div>
            <div className="w-full bg-card-200 dark:bg-card-700 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-500 dark:bg-blue-400 h-full transition-all duration-300 ease-out"
                style={{ width: `${exportProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Format Selector */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Export Format</Label>
          <RadioGroup
            value={selectedFormat}
            onValueChange={(value: string) => setSelectedFormat(value as ExportFormat)}
          >
            <div className="flex items-center space-x-2 p-2 rounded-md hover:bg-card-50 dark:hover:bg-card-900">
              <RadioGroupItem value="csv" id="csv" />
              <Label htmlFor="csv" className="cursor-pointer flex-1">
                <div className="font-medium">📄 CSV - Spreadsheet</div>
                <div className="text-xs text-gray-500">Excel, Google Sheets compatible</div>
              </Label>
            </div>
            <div className="flex items-center space-x-2 p-2 rounded-md hover:bg-card-50 dark:hover:bg-card-900">
              <RadioGroupItem value="pdf" id="pdf" />
              <Label htmlFor="pdf" className="cursor-pointer flex-1">
                <div className="font-medium">📊 PDF - Formatted Report</div>
                <div className="text-xs text-gray-500">Print-ready, fixed layout</div>
              </Label>
            </div>
            <div className="flex items-center space-x-2 p-2 rounded-md hover:bg-card-50 dark:hover:bg-card-900">
              <RadioGroupItem value="docx" id="docx" />
              <Label htmlFor="docx" className="cursor-pointer flex-1">
                <div className="font-medium">📝 DOCX - Editable Document</div>
                <div className="text-xs text-gray-500">Microsoft Word compatible</div>
              </Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleCurrentPage}
            disabled={isLoading}
            className="w-full justify-between"
            variant="default"
          >
            <span className="flex items-center gap-2">
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Export Current Page
            </span>
            <span className="text-xs opacity-70 font-normal">
              {currentPageCount} items {isGrouped && "• Analysis Ready"}
            </span>
          </Button>

          <Button
            onClick={handleAllFiltered}
            disabled={isLoading}
            variant="outline"
            className="w-full justify-between"
            title={
              hasMorePages
                ? hasActiveFilters
                  ? "Fetches and exports all filtered items"
                  : "Fetches and exports all items"
                : "No additional pages to export"
            }
          >
            <span className="flex items-center gap-2">
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Export {hasActiveFilters ? "All Filtered" : "All"} Results
            </span>
            <span className="text-xs opacity-70 font-normal">
              {totalCount} items {isGrouped && "• Analysis Ready"}
            </span>
          </Button>
        </div>

        {hasMorePages && (
          <div className="bg-card-50 dark:bg-card-900 p-3 rounded-md text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-800">
            <p className="font-medium mb-1">💡 To Export All Pages:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Use filters to narrow down the dataset</li>
              <li>Export the current page</li>
              <li>Go to the next page and repeat</li>
              <li>Combine the CSV files</li>
            </ul>
          </div>
        )}

        <Button onClick={onCancel} disabled={isLoading} variant="ghost" className="w-full">
          Cancel
        </Button>
      </div>
    </ResponsiveModal>
  );
};
